import { useCallback } from "react";
import { Form } from "react-final-form";
import { Layout, Page, Stack, Text, Columns } from "@shopify/polaris";
import StepsButtonActions from "../../../../../common/StepsButtonActions/StepsButtonActions.js";
import OutOfStockCard from "./components/OutOfStockCard/OutOfStockCard.js";
import LowStockCard from "./components/LowStockCard/LowStockCard.js";
import BackStockCard from "./components/BackStockCard/BackStockCard.js";
import UpdatesFrequencyCard from "../UpdatesFrequencyCard/UpdatesFrequencyCard.js";
import { nextStep, updateStockOptions, updateUpdatesFrequency } from "../../../../../../store/features/action.js";
import { validation } from "./validation.js";
import { PRODUCTS_ACTION_PUSH_DOWN } from "../../../../../../../constants/productActionTypes.js";
import { openModal } from "../../../../../../store/features/modal.js";
import { OPEN_MODAL_COLLECTION_CHANGE_CONFIRM_MODAL } from "../../../../../../constants/modalTypes.js";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks/redux.js";
import NotificationPreviewModal from "../../../../../modals/NotificationPreviewModal/NotificationPreviewModal.js";
import useHandleKeyDown from "../../../../../../hooks/useHandleKeyDown.js";
import ValidationErrorsListBanner from "../../../../../common/ValidationErrorsListBanner/ValidationErrorsListBanner.js";

const StockTypeAndOptions = () => {
  const dispatch = useAppDispatch();
  const stockOptions = useAppSelector((state) => state.action.actionData.options.stockOptions);
  const updatesFrequency = useAppSelector((state) => state.action.actionData.updatesFrequency);

  const onSubmit = useCallback((data) => {
    if (data.hasValidationErrors) return;

    dispatch(updateUpdatesFrequency(data.updatesFrequency));
    const dataWithoutUpdatesFrequency = { ...data };
    delete dataWithoutUpdatesFrequency.updatesFrequency;
    dispatch(updateStockOptions(dataWithoutUpdatesFrequency));

    if (data.outStockAction === PRODUCTS_ACTION_PUSH_DOWN || data.lowStockAction === PRODUCTS_ACTION_PUSH_DOWN) {
      dispatch(openModal({ modalType: OPEN_MODAL_COLLECTION_CHANGE_CONFIRM_MODAL }));
      return;
    }

    dispatch(nextStep());
  }, []);

  const handleKeyDown = useHandleKeyDown();

  return (
    <Page fullWidth>
      <NotificationPreviewModal />
      <Form
        onSubmit={onSubmit}
        initialValues={{ ...stockOptions, updatesFrequency }}
        validate={validation}
        render={({ handleSubmit, hasValidationErrors, submitFailed, errors }) => {
          const shouldShowBanner = hasValidationErrors && submitFailed;
          const errorMessages = Object.values(errors);
          return (
            <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
              <Stack vertical>
                <ValidationErrorsListBanner errors={errorMessages} show={shouldShowBanner} />
                <StepsButtonActions disabled={false} isNextLoading={false} />
                <Layout>
                  <Layout.Section>
                    <Columns columns={{ xs: "1fr 2fr" }}>
                      <Stack vertical spacing="tight">
                        <Text variant="headingMd" as="p">Out of Stock</Text>
                        <Text variant="bodySm" as="p">
                          Run an action when the stock of a product you’ve selected is lower than your selected Stock level.
                        </Text>
                      </Stack>
                      <OutOfStockCard />
                    </Columns>
                  </Layout.Section>
                  <Layout.Section>
                    <Columns columns={{ xs: "1fr 2fr" }}>
                      <Stack vertical spacing="tight">
                        <Text variant="headingMd" as="p">Low Stock</Text>
                        <Text variant="bodySm" as="p">
                          Run an action when the stock of a product you’ve selected is in your selected Stock level range.
                        </Text>
                      </Stack>
                      <LowStockCard />
                    </Columns>
                  </Layout.Section>
                  <Layout.Section>
                    <Columns columns={{ xs: "1fr 2fr" }}>
                      <Stack vertical spacing="tight">
                        <Text variant="headingMd" as="p">Back in Stock</Text>
                        <Text variant="bodySm" as="p">
                          Run an action when the stock of a product has recovered over a specific stock level.
                        </Text>
                      </Stack>
                      <BackStockCard />
                    </Columns>
                  </Layout.Section>
                  <Layout.Section>
                    <Columns columns={{ xs: "1fr 2fr" }}>
                      <Stack vertical spacing="tight">
                        <Text variant="headingMd" as="p">Updates Frequency</Text>
                        <Text variant="bodySm" as="p">
                          Set how often you’d like to check for stock updates and take action accordingly.
                        </Text>
                      </Stack>
                      <UpdatesFrequencyCard />
                    </Columns>
                  </Layout.Section>
                </Layout>
              </Stack>
            </form>
          );
        }}
      />
    </Page>
  );
};

export default StockTypeAndOptions;